import { Controller } from "@hotwired/stimulus"

// The native select stays the form field and what other scripts read and
// set; people pick from the ContextMenu instead. A pick writes the value back
// and fires change on the select, so the page reacts as it always did.
export default class extends Controller {
  static targets = ["select", "trigger", "value", "option"]

  // A script can set the select's value before this controller is listening —
  // Stimulus connects the panel that drives it first — so the trigger reads
  // its label off the select rather than trusting the one the server rendered.
  // Read-only fields have no select at all.
  connect() {
    if (this.hasSelectTarget && this.hasValueTarget) this.sync()
  }

  pick(event) {
    const option = event.target
    if (!this.optionTargets.includes(option.closest("label"))) return

    this.selectTarget.value = option.value
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.sync()
    this.close()
  }

  // Scripts that set the select's value announce it with an input event.
  sync() {
    const value = this.selectTarget.value
    this.radios.forEach(radio => { radio.checked = radio.value === value })
    const picked = this.radios.find(radio => radio.checked)
    this.valueTarget.textContent = picked ? picked.closest("label").textContent.trim() : ""
  }

  openWithKeys(event) {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return

    event.preventDefault()
    if (!this.isOpen) this.triggerTarget.click()
    requestAnimationFrame(() => (this.radios.find(radio => radio.checked) || this.radios[0])?.focus())
  }

  move(event) {
    const index = this.radios.indexOf(document.activeElement)
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.radios[(index + step + this.radios.length) % this.radios.length]?.focus()
    } else if (event.key === "Enter") {
      event.preventDefault()
      document.activeElement?.click()
    } else if (event.key === "Escape") {
      this.close()
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      const letter = event.key.toLowerCase()
      const after = [...this.radios.slice(index + 1), ...this.radios.slice(0, index + 1)]
      after.find(radio => radio.closest("label").textContent.trim().toLowerCase().startsWith(letter))?.focus()
    }
  }

  close() {
    if (this.isOpen) this.triggerTarget.click()
    this.triggerTarget.focus()
  }

  get isOpen() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true"
  }

  get radios() {
    return this.optionTargets.map(option => option.querySelector("input"))
  }
}
